import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IOptions } from '../../../common/common.types'
import { IMedicalConsultState } from '../../procedures/medicalConsult/store/medicalConsult.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateMedicalConsultProcedureProps {
  procedureEvent: IEKGEvent
  medicalConsultOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface MedicalConsultProcedureObject {
  medicalConsultState: IMedicalConsultState
  timelineItem: IItem
}

const createMedicalConsultProcedure = (props: CreateMedicalConsultProcedureProps): MedicalConsultProcedureObject => {
  const { procedureEvent, medicalConsultOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const itemComments = procedureEvent.procedureComments ?? ''
  const medicalConsultMethodOption = medicalConsultOptions.find(
    (option) => (option.name ?? '').toString() === procedureEvent.procedureAction
  )
  const itemSubHeader = `By , Physician: , Method: ${
    medicalConsultMethodOption?.name ?? ''
  }, Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.MEDICAL_CONSULT_PROCEDURE,
    medicalConsultMethodOption?.name || '',
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Medical Consult',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_MEDICAL_CONSULT_PROCEDURE_ITEM,
    defaultHref: TabConstants.MEDICAL_CONSULT_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let medicalConsultState: IMedicalConsultState = {
    key: clientEventId,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    physician: null,
    otherPhysician: '',
    method: medicalConsultMethodOption || null,
    facility: null,
    assessedBy: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    type: 'Medical Consult',
    rolePerforming: null,
    rolePerformingPNNV: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
    isEkgImport: 1,
    medicalConsultDefaults: null,
    nemsisCode: null,
  }

  try {
    medicalConsultState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: medicalConsultState,
      actionField: (s) => s.method?.name,
      filter1Field: null,
      filter2Field: null,
      filter3Field: null,
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    medicalConsultState = handleNemsisCodeError(error, medicalConsultState)
  }

  const newMedicalConsultProcedureObject: MedicalConsultProcedureObject = {
    medicalConsultState,
    timelineItem,
  }

  return newMedicalConsultProcedureObject
}

export default createMedicalConsultProcedure
